import {
    useMutation,
    useQuery,
} from "@tanstack/react-query";
import {
    ChevronLeft,
    ChevronRight,
    Eye,
    Link2,
    Loader2,
    Trash2,
    Upload,
} from "lucide-preact";
import {
    useCallback,
    useEffect,
    useState,
} from "preact/compat";
import {
    toast,
} from "react-hot-toast";
import {
    useSearchParams,
} from "react-router-dom";

import {
    splClient,
} from "../../api/client.ts";
import type {
    SortBy,
} from "../../stores/manageStore.ts";
import {
    SORT_BY_VALUES,
    SORT_ORDER_VALUES,
    useManageStore,
} from "../../stores/manageStore.ts";

import {
    Button,
} from "@mrflark/ui";
import {
    BulkActionsBar,
    MediaGrid,
    MediaPreviewPopup,
    usePreviewPopup,
} from "../media/index.ts";
import {
    TagQueryInput,
} from "../tags/TagQueryInput.tsx";
import {
    UploadModal,
} from "./UploadModal.tsx";

const generatePageNumbers = (
    current: number,
    total: number,
): (number | string)[] => {
    if (total <= 7) {
        return Array.from({
            length: total,
        }, (_, index) => index + 1);
    }

    const pages: (number | string)[] = [];
    pages.push(1);

    if (current > 3) {
        pages.push("...");
    }

    for (
        let pageNum = Math.max(2, current - 1);
        pageNum <= Math.min(total - 1, current + 1);
        pageNum++
    ) {
        pages.push(pageNum);
    }

    if (current < total - 2) {
        pages.push("...");
    }

    if (total > 1) {
        pages.push(total);
    }

    return pages;
};

export const LibraryTab = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showImportInput, setShowImportInput] = useState(false);
    const [importUrl, setImportUrl] = useState("");

    const {
        viewMode,
        showDeleted,
        thumbnailSize,
        sortBy,
        sortOrder,
        tagQuery,
        page,
        perPage,
        selectedIds,
        setShowDeleted,
        setSort,
        setTagQuery,
        setPage,
        setPerPage,
        toggleSelection,
        selectAll,
        clearSelection,
    } = useManageStore();

    const {
        previewItem,
        previewPosition,
        showPreview,
        hidePreview,
        updatePosition,
    } = usePreviewPopup();

    // sync state from URL on mount
    useEffect(() => {
        const urlView = searchParams.get("view");
        const urlTagQuery = searchParams.get("tag_query");
        const urlSortBy = searchParams.get("sort_by");
        const urlSortOrder = searchParams.get("sort_order");
        const urlPage = searchParams.get("page");
        const urlPerPage = searchParams.get("per_page");

        if (urlView === "deleted") setShowDeleted(true);
        if (urlTagQuery) setTagQuery(urlTagQuery);
        if (urlSortBy && SORT_BY_VALUES.includes(urlSortBy as SortBy)) {
            const validSortOrder = urlSortOrder &&
                    SORT_ORDER_VALUES.includes(urlSortOrder as "asc" | "desc")
                ? (urlSortOrder as "asc" | "desc")
                : undefined;
            setSort(urlSortBy as SortBy, validSortOrder);
        }
        if (urlPage) setPage(parseInt(urlPage));
        if (urlPerPage) setPerPage(parseInt(urlPerPage));
    }, []);

    // update URL when state changes, preserving tab param
    useEffect(() => {
        const params = new URLSearchParams();
        const currentTab = searchParams.get("tab");
        if (currentTab) params.set("tab", currentTab);
        if (showDeleted) params.set("view", "deleted");
        if (tagQuery) params.set("tag_query", tagQuery);
        if (sortBy !== "id") params.set("sort_by", sortBy);
        if (sortOrder !== "asc") params.set("sort_order", sortOrder);
        if (page !== 1) params.set("page", String(page));
        if (perPage !== 50) params.set("per_page", String(perPage));
        setSearchParams(params, {
            replace: true,
        });
    }, [
        showDeleted,
        tagQuery,
        sortBy,
        sortOrder,
        page,
        perPage,
        setSearchParams,
    ]);

    const {
        data,
        isLoading,
        refetch,
    } = useQuery({
        queryKey: ["manageItems", {
            showDeleted,
            tagQuery,
            sortBy,
            sortOrder,
            page,
            perPage,
        }],
        queryFn: () =>
            splClient.getManageItems({
                view: showDeleted ? "deleted" : "active",
                tagQuery: tagQuery || undefined,
                sortBy,
                sortOrder,
                page,
                perPage,
            }),
    });

    const importMutation = useMutation({
        mutationFn: (url: string) => splClient.importMedia(url),
        onSuccess: () => {
            toast.success("Import started");
            setImportUrl("");
            setShowImportInput(false);
            refetch();
        },
        onError: (error: Error) =>
            toast.error(`Import failed: ${error.message}`),
    });

    const items = data?.items || [];
    const totalItems = data?.total_items || 0;
    const totalPages = data?.total_pages || 1;

    const [focusedIndex, setFocusedIndex] = useState(-1);

    // reset focus when items change
    useEffect(() => {
        setFocusedIndex(-1);
    }, [data]);

    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        const target = event.target as HTMLElement;
        if (
            target.tagName === "INPUT" || target.tagName === "TEXTAREA" ||
            target.isContentEditable
        ) return;

        if (event.key === "j" || event.key === "ArrowDown") {
            event.preventDefault();
            setFocusedIndex((prev) => Math.min(prev + 1, items.length - 1));
        } else if (event.key === "k" || event.key === "ArrowUp") {
            event.preventDefault();
            setFocusedIndex((prev) => Math.max(prev - 1, 0));
        } else if (
            event.key === "Enter" && focusedIndex >= 0 &&
            focusedIndex < items.length
        ) {
            event.preventDefault();
            globalThis.open(items[focusedIndex].media_url, "_blank");
        } else if (event.key === "Escape") {
            setFocusedIndex(-1);
        }
    }, [items, focusedIndex]);

    useEffect(() => {
        globalThis.addEventListener("keydown", handleKeyDown);
        return () => globalThis.removeEventListener("keydown", handleKeyDown);
    }, [handleKeyDown]);

    const handleItemClick = (item: { media_url: string; }): void => {
        globalThis.open(item.media_url, "_blank");
    };

    const handleSelectAll = (): void => {
        selectAll(items.map((item) => item.id));
    };

    const handleSort = (column: string): void => {
        if (SORT_BY_VALUES.includes(column as SortBy)) {
            setSort(column as SortBy);
        }
    };

    return (
        <>
            {/* Filters */}
            <div className="max-w-6xl mx-auto p-4 space-y-4">
                <div className="flex flex-wrap items-end gap-4">
                    <div className="flex gap-2">
                        <Button
                            variant={!showDeleted ? "primary" : "secondary"}
                            size="sm"
                            onClick={() => setShowDeleted(false)}
                            className="gap-1"
                        >
                            <Eye size={16} />
                            Active
                        </Button>
                        <Button
                            variant={showDeleted ? "error" : "secondary"}
                            size="sm"
                            onClick={() => setShowDeleted(true)}
                            className="gap-1"
                        >
                            <Trash2 size={16} />
                            Deleted
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setShowUploadModal(true)}
                            className="gap-1"
                        >
                            <Upload size={16} />
                            Upload
                        </Button>
                        <Button
                            variant={showImportInput ? "primary" : "secondary"}
                            size="sm"
                            onClick={() => setShowImportInput(!showImportInput)}
                            className="gap-1"
                        >
                            <Link2 size={16} />
                            Import URL
                        </Button>
                    </div>

                    <div className="flex-1 min-w-[300px]">
                        <TagQueryInput
                            value={tagQuery}
                            onChange={setTagQuery}
                            onSubmit={() => refetch()}
                            placeholder="Filter by tag query..."
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="text-sm text-text-secondary">
                            Per page:
                        </span>
                        <select
                            value={perPage}
                            onChange={(event) =>
                                setPerPage(
                                    parseInt(
                                        (event.target as HTMLInputElement)
                                            .value,
                                    ),
                                )}
                            className="input py-1.5"
                        >
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={200}>200</option>
                        </select>
                    </div>
                </div>

                {showImportInput && (
                    <div className="flex items-center gap-2">
                        <input
                            type="url"
                            value={importUrl}
                            onChange={(event) =>
                                setImportUrl(
                                    (event.target as HTMLInputElement).value,
                                )}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" && importUrl.trim()) {
                                    importMutation.mutate(importUrl.trim());
                                }
                            }}
                            placeholder="https://example.com/image.jpg"
                            className="input flex-1"
                            disabled={importMutation.isPending}
                        />
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() =>
                                importMutation.mutate(importUrl.trim())}
                            disabled={!importUrl.trim() ||
                                importMutation.isPending}
                            className="gap-1"
                        >
                            {importMutation.isPending
                                ? <Loader2 size={14} className="animate-spin" />
                                : <Link2 size={14} />}
                            Import
                        </Button>
                    </div>
                )}

                <div className="flex items-center justify-between text-sm text-text-secondary">
                    <span>
                        Showing{" "}
                        {items.length > 0 ? (page - 1) * perPage + 1 : 0}-
                        {Math.min(page * perPage, totalItems)} of {totalItems}
                        {" "}
                        items
                    </span>
                    {selectedIds.size > 0 && (
                        <span className="text-accent">
                            {selectedIds.size} selected
                        </span>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="max-w-6xl mx-auto px-4">
                <MediaGrid
                    items={items}
                    selectedIds={selectedIds}
                    onToggleSelect={toggleSelection}
                    onSelectAll={handleSelectAll}
                    viewMode={viewMode}
                    thumbnailSize={thumbnailSize}
                    onItemClick={handleItemClick}
                    onItemHover={showPreview}
                    onItemLeave={hidePreview}
                    onMouseMove={updatePosition}
                    isLoading={isLoading}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    focusedIndex={focusedIndex}
                />
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="max-w-6xl mx-auto px-4 py-6 flex items-center justify-center gap-2">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setPage(page - 1)}
                        disabled={page <= 1}
                    >
                        <ChevronLeft size={18} />
                    </Button>

                    <div className="flex items-center gap-1">
                        {generatePageNumbers(page, totalPages).map((
                            pageNum,
                            index,
                        ) => pageNum === "..."
                            ? (
                                <span
                                    key={`ellipsis-${index}`}
                                    className="px-2 text-text-muted"
                                >
                                    ...
                                </span>
                            )
                            : (
                                <button
                                    type="button"
                                    key={pageNum}
                                    onClick={() => setPage(pageNum as number)}
                                    className={`px-3 py-1 rounded transition-colors ${
                                        page === pageNum
                                            ? "bg-accent text-white"
                                            : "bg-bg-tertiary text-text-primary hover:bg-border"
                                    }`}
                                >
                                    {pageNum}
                                </button>
                            )
                        )}
                    </div>

                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setPage(page + 1)}
                        disabled={page >= totalPages}
                    >
                        <ChevronRight size={18} />
                    </Button>
                </div>
            )}

            {/* Preview popup */}
            {previewItem && (
                <MediaPreviewPopup
                    item={previewItem}
                    position={previewPosition}
                    onClose={hidePreview}
                />
            )}

            {/* Bulk actions bar */}
            <BulkActionsBar
                selectedIds={Array.from(selectedIds)}
                onClearSelection={clearSelection}
                isDeleted={showDeleted}
                onActionComplete={() => refetch()}
            />

            <UploadModal
                isOpen={showUploadModal}
                onClose={() => setShowUploadModal(false)}
            />
        </>
    );
};
